import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { PackageIcon, UserIcon, TrendingUpIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { supabase } from '../../utils/supabase';
import { useTheme } from '../../contexts/ThemeContext';
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    category: row.category,
    status: row.is_archived ? 'archived' as any : row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
const BAR_COLORS = [['#f59e0b', '#ea580c'], ['#6366f1', '#8b5cf6'], ['#10b981', '#059669'], ['#3b82f6', '#2563eb'], ['#ec4899', '#db2777']];
const MEDALS = ['🥇', '🥈', '🥉'];
export function DashboardProductRevenue() {
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [loading, setLoading] = useState(true);
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const chartAxisColor = isDark ? '#9ca3af' : '#9ca3af';
  const chartTickColor = isDark ? '#d1d5db' : '#374151';
  const chartTooltipBg = isDark ? '#1f2937' : '#ffffff';
  const chartTooltipBorder = isDark ? '1px solid #374151' : 'none';
  const chartTooltipColor = isDark ? '#f9fafb' : '#111827';
  const metaChipBg = isDark ? '#1f2937' : '#f3f4f6';
  const metaChipColor = isDark ? '#9ca3af' : '#9ca3af';
  const progressTrackBg = isDark ? '#1f2937' : '#f3f4f6';
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(getToday());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{
          data: productsData,
          error: prodErr
        }, {
          data: salesData,
          error: saleErr
        }] = await Promise.all([supabase.from('products').select('*').order('created_at', {
          ascending: false
        }), supabase.from('product_sales').select('*').order('date', {
          ascending: false
        })]);
        if (prodErr) throw prodErr;
        if (saleErr) throw saleErr;
        if (!cancelled) {
          setProducts((productsData ?? []).map(rowToProduct));
          setSales((salesData ?? []).map(rowToSale));
        }
      } catch (e) {
        logger.error('DashboardProductRevenue:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  /* ── stats by USER (top 5) ──────────────────────────────────────────── */
  const userStats = useMemo(() => {
    const filtered = sales.filter(s => (!dateFrom || s.date >= dateFrom) && (!dateTo || s.date <= dateTo));
    const map: Record<string, {
      name: string;
      amount: number;
      sales: number;
      units: number;
    }> = {};
    filtered.forEach(sale => {
      const uid = sale.createdByUserId || 'unknown';
      const name = sale.createdByUserName || 'Usuario';
      if (!map[uid]) map[uid] = {
        name,
        amount: 0,
        sales: 0,
        units: 0
      };
      map[uid].amount += sale.total;
      map[uid].sales += 1;
      map[uid].units += sale.items.reduce((s: number, i: any) => s + Number(i.quantity ?? 0), 0);
    });
    return Object.values(map).sort((a, b) => b.amount - a.amount).slice(0, 5); // ← top 5
  }, [sales, dateFrom, dateTo]);
  const totalRevenue = userStats.reduce((s, u) => s + u.amount, 0);
  const maxAmount = Math.max(...userStats.map(u => u.amount), 1);
  return <Card padding="none" className="overflow-hidden" data-id="element-2705">
      <div className="h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" data-id="element-2706" />
      <div className="p-4 md:p-6" data-id="element-2707">
        {/* Header */}
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2708">
          <CardTitle className="flex items-center gap-2" data-id="element-2709">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20" data-id="element-2710">
              <PackageIcon className="w-5 h-5 text-white" data-id="element-2711" />
            </div>
            <div data-id="element-2712">
              <span className="block" data-id="element-2713">Ingresos por Productos</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-2714">
                Top 5 vendedores del período
              </span>
            </div>
          </CardTitle>
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white py-1.5 px-3 text-sm shadow-lg shadow-amber-500/20 border-none self-start md:self-auto" data-id="element-2715">
            {loading ? '...' : formatCurrency(totalRevenue)}
          </Badge>
        </CardHeader>

        {/* Date filter */}
        <div className="flex flex-wrap items-center gap-2 mt-3 mb-5" data-id="element-2716">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2717">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-2718" />
            
            <span className="text-gray-300 dark:text-gray-600 text-xs" data-id="element-2719">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-2720" />
            
          </div>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-2721">
          {loading ? <div className="space-y-4 py-2 animate-pulse" data-id="element-2722">
              {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-3" data-id="element-2723">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0" data-id="element-2724" />
                  <div className="flex-1 space-y-2" data-id="element-2725">
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" data-id="element-2726" />
                    <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-full" data-id="element-2727" />
                  </div>
                </div>)}
            </div> : userStats.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm italic" data-id="element-2728">
              No se encontraron ventas en este período.
            </div> : <>
              {/* Bar chart */}
              <div className="mb-6" data-id="element-2729">
                <ResponsiveContainer width="100%" height={220} data-id="element-2730">
                  <BarChart data={userStats} layout="vertical" margin={{
                left: 0,
                right: 10
              }} data-id="element-2731">
                  
                    <defs data-id="element-2732">
                      {BAR_COLORS.map(([c1, c2], i) => <linearGradient key={i} id={`ug${i}`} x1="0" y1="0" x2="1" y2="0" data-id="element-2733">
                      
                          <stop offset="0%" stopColor={c1} data-id="element-2734" />
                          <stop offset="100%" stopColor={c2} data-id="element-2735" />
                        </linearGradient>)}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} horizontal vertical={false} data-id="element-2736" />
                  
                    <XAxis type="number" stroke={chartAxisColor} fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2737" />
                  
                    <YAxis dataKey="name" type="category" stroke={chartAxisColor} fontSize={12} tickLine={false} axisLine={false} width={110} tick={{
                  fill: chartTickColor,
                  fontWeight: 500
                }} data-id="element-2738" />
                  
                    <Tooltip contentStyle={{
                  backgroundColor: chartTooltipBg,
                  border: chartTooltipBorder,
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  padding: '10px 14px',
                  color: chartTooltipColor
                }} formatter={(value: number, _: any, props: any) => {
                  const entry = userStats[props.index] ?? {};
                  return [<span data-id="element-2740">
                            <span style={{
                      fontWeight: 'bold',
                      color: chartTooltipColor
                    }} data-id="element-2741">
                          
                              {formatCurrency(value)}
                            </span>
                            <span className="text-gray-400 text-xs ml-2" data-id="element-2742">
                              {entry.sales} venta{entry.sales !== 1 ? 's' : ''}{' '}
                              · {entry.units} unid.
                            </span>
                          </span>, <span style={{
                    color: chartTooltipColor
                  }} data-id="element-2743">
                        
                            Ingresos
                          </span>];
                }} data-id="element-2739" />
                  

                    <Bar dataKey="amount" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-2744">
                      {userStats.map((_, i) => <Cell key={i} fill={`url(#ug${i})`} data-id="element-2745" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Ranked list */}
              <div className="space-y-3" data-id="element-2746">
                {userStats.map((user, index) => <div key={user.name} className="flex items-center gap-3 px-1" data-id="element-2747">
                    {/* rank badge */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm`} style={{
                background: `linear-gradient(135deg, ${BAR_COLORS[index][0]}, ${BAR_COLORS[index][1]})`,
                color: 'white'
              }} data-id="element-2748">
                  
                      {index < 3 ? MEDALS[index] : index + 1}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-2749">
                      {/* name + amount */}
                      <div className="flex justify-between items-center mb-1.5" data-id="element-2750">
                        <div className="flex items-center gap-1.5 min-w-0" data-id="element-2751">
                          <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2752" />
                          <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-2753">
                            {user.name}
                          </span>
                        </div>
                        <span className="text-[14px] font-bold ml-2 whitespace-nowrap" style={{
                    color: BAR_COLORS[index][1]
                  }} data-id="element-2754">
                      
                          {formatCurrency(user.amount)}
                        </span>
                      </div>

                      {/* progress bar */}
                      <div className="w-full rounded-full h-2 overflow-hidden mb-1.5" style={{
                  backgroundColor: progressTrackBg
                }} data-id="element-2755">
                    
                        <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
                    width: `${user.amount / maxAmount * 100}%`,
                    background: `linear-gradient(90deg, ${BAR_COLORS[index][0]}, ${BAR_COLORS[index][1]})`
                  }} data-id="element-2756" />
                    
                      </div>

                      {/* meta chips */}
                      <div className="flex items-center gap-2" data-id="element-2757">
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{
                    color: metaChipColor,
                    backgroundColor: metaChipBg
                  }} data-id="element-2758">
                      
                          {user.sales} venta{user.sales !== 1 ? 's' : ''}
                        </span>
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{
                    color: metaChipColor,
                    backgroundColor: metaChipBg
                  }} data-id="element-2759">
                      
                          {user.units} unid.
                        </span>
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full flex items-center gap-1" style={{
                    color: metaChipColor,
                    backgroundColor: metaChipBg
                  }} data-id="element-2760">
                      
                          <TrendingUpIcon className="w-2.5 h-2.5" data-id="element-2761" />
                          prom. {formatCurrency(user.amount / user.sales)}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </>}
        </CardContent>
      </div>
    </Card>;
}